import { UserManager, WebStorageStateStore } from "oidc-client-ts";

import stringHasValue from "./string-has-value";

const defaultCallbackPath = "/auth";
const defaultScopes = ["openid", "offline_access"];
const authLogPrefix = "[auth][oidc]";

let manager = null;
let oidcConfig = null;
let bypassAuthEnabled = false;
let redirectInProgress = false;
let callbackInProgress = false;
let authReady = false;
let authReadyPromise = null;
let tokenRefreshPromise = null;
let pendingPostSignInReturnUrl = null;

const logInfo = (message, details) => {
  if (details === undefined) {
    console.info(`${authLogPrefix} ${message}`); // eslint-disable-line no-console
    return;
  }

  console.info(`${authLogPrefix} ${message}`, details); // eslint-disable-line no-console
};

const logWarn = (message, details) => {
  if (details === undefined) {
    console.warn(`${authLogPrefix} ${message}`); // eslint-disable-line no-console
    return;
  }

  console.warn(`${authLogPrefix} ${message}`, details); // eslint-disable-line no-console
};

const logError = (message, error) => {
  if (!error) {
    console.error(`${authLogPrefix} ${message}`); // eslint-disable-line no-console
    return;
  }

  console.error(`${authLogPrefix} ${message}`, { // eslint-disable-line no-console
    message: error?.message,
    name: error?.name
  });
};

const normalizePath = (path) => {
  if (!stringHasValue(path)) {
    return "/";
  }
  return path.startsWith("/") ? path : `/${path}`;
};

const buildAbsoluteUri = (path) => {
  const normalizedPath = normalizePath(path);
  return new URL(normalizedPath, window.location.origin).toString();
};

const getScopeString = (scopes) => {
  if (!Array.isArray(scopes)) {
    return defaultScopes.join(" ");
  }

  const configuredScopes = Array.from(new Set(scopes.filter((scope) => stringHasValue(scope))));
  if (configuredScopes.length === 0) {
    return defaultScopes.join(" ");
  }

  return configuredScopes.join(" ");
};

const isConfiguredForOidc = () =>
  !bypassAuthEnabled &&
  stringHasValue(oidcConfig?.authority) &&
  stringHasValue(oidcConfig?.clientId);

const isLocalHost = (hostname) => hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

const canUseBrowserStorageForOidc = () => {
  const { protocol, hostname } = window.location;
  return protocol === "https:" || isLocalHost(hostname);
};

const getValidatedSessionStorage = () => {
  if (!canUseBrowserStorageForOidc()) {
    throw new Error("OIDC browser storage requires HTTPS (except localhost).");
  }

  const storage = window.sessionStorage;
  const probeKey = "__oidc_storage_probe__";
  storage.setItem(probeKey, "1");
  storage.removeItem(probeKey);
  return storage;
};

const ensureManager = () => {
  if (!isConfiguredForOidc()) {
    return null;
  }

  if (!manager) {
    const sessionStorageStore = getValidatedSessionStorage();
    manager = new UserManager({
      authority: oidcConfig.authority,
      client_id: oidcConfig.clientId,
      redirect_uri: buildAbsoluteUri(oidcConfig.callbackPath ?? defaultCallbackPath),
      post_logout_redirect_uri: stringHasValue(oidcConfig.signOutAppUrl)
        ? oidcConfig.signOutAppUrl
        : buildAbsoluteUri("/"),
      response_type: "code",
      scope: getScopeString(oidcConfig.scopes),
      userStore: new WebStorageStateStore({ store: sessionStorageStore }),
      stateStore: new WebStorageStateStore({ store: sessionStorageStore }),
      automaticSilentRenew: false,
      loadUserInfo: true,
      filterProtocolClaims: true,
      // Explicit PKCE configuration for OAuth 2.0 Authorization Code Flow with PKCE
      // PKCE (Proof Key for Code Exchange) prevents authorization code interception attacks
      // S256 is the recommended code challenge method (SHA-256 hash)
      code_challenge_method: "S256"
    });

    manager.events.addUserLoaded((user) => {
      const expiresAt = user?.expires_at;
      authReady = true;
      logInfo("OIDC user loaded", { expiresAt });
    });
    manager.events.addUserUnloaded(() => {
      authReady = false;
      logWarn("OIDC user unloaded");
    });
    manager.events.addAccessTokenExpired(() => {
      logWarn("OIDC access token expired");
    });
    manager.events.addSilentRenewError((error) => {
      logError("OIDC silent renew error", error);
    });
  }

  return manager;
};

const getCurrentRoute = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const isSigninCallbackRequest = () => {
  const callbackPath = normalizePath(oidcConfig?.callbackPath ?? defaultCallbackPath);
  if (window.location.pathname !== callbackPath) {
    return false;
  }

  const query = new URLSearchParams(window.location.search);
  return query.has("code") || query.has("error");
};

export const configureOidcAuth = (configuration) => {
  const config = configuration ?? {};
  bypassAuthEnabled = config.bypassAuthEnabled === true;
  oidcConfig = config.auth ?? null;
  manager = null;
  redirectInProgress = false;
  callbackInProgress = false;
  authReady = false;
  authReadyPromise = null;
  tokenRefreshPromise = null;
  pendingPostSignInReturnUrl = null;
  logInfo("OIDC config loaded", {
    bypassAuthEnabled,
    authorityConfigured: stringHasValue(oidcConfig?.authority),
    clientIdConfigured: stringHasValue(oidcConfig?.clientId),
    callbackPath: oidcConfig?.callbackPath ?? defaultCallbackPath
  });
};

export const canStartAuthenticationRedirect = () =>
  !bypassAuthEnabled &&
  !callbackInProgress &&
  !redirectInProgress &&
  !isSigninCallbackRequest();

export const ensureAuthenticated = async () => {
  if (bypassAuthEnabled) {
    authReady = true;
    return true;
  }

  const userManager = ensureManager();
  if (!userManager) {
    throw new Error("OIDC authentication is not configured.");
  }

  if (isSigninCallbackRequest()) {
    callbackInProgress = true;
    try {
      const callbackUser = await userManager.signinRedirectCallback();
      const returnUrl = callbackUser?.state?.returnUrl;
      const safeReturnUrl = stringHasValue(returnUrl) && returnUrl.startsWith("/") ? returnUrl : "/";
      logInfo("OIDC callback completed", { safeReturnUrl });
      window.history.replaceState({}, document.title, safeReturnUrl);
      pendingPostSignInReturnUrl = safeReturnUrl;
    } catch (error) {
      logError("OIDC callback failed", error);
      await userManager.clearStaleState();
      callbackInProgress = false;
      await signIn(getCurrentRoute());
      return false;
    }
    callbackInProgress = false;
  }

  const user = await userManager.getUser();
  if (user && !user.expired) {
    authReady = true;
    logInfo("Existing OIDC session found", { expiresAt: user.expires_at });
    return true;
  }

  authReady = false;
  logWarn("No valid OIDC user in storage. Starting redirect.");
  await userManager.signinRedirect({
    state: {
      returnUrl: getCurrentRoute()
    }
  });
  return false;
};

//the sign-in callback restores the pre-login url with history.replaceState, which
//react-router never observes — its location still points at the /auth callback path.
//the app must consume this after authentication and navigate the router itself, or
//the url the user actually requested is lost to the last-active landing redirect.
export const consumePostSignInReturnUrl = () => {
  const returnUrl = pendingPostSignInReturnUrl;
  pendingPostSignInReturnUrl = null;
  return returnUrl;
};

export const waitForAuthReady = async () => {
  if (bypassAuthEnabled) {
    return true;
  }

  if (authReady) {
    return true;
  }

  if (!authReadyPromise) {
    authReadyPromise = ensureAuthenticated()
      .then((authenticated) => {
        authReady = authenticated === true;
        return authenticated;
      })
      .finally(() => {
        if (!authReady) {
          authReadyPromise = null;
        }
      });
  }

  return authReadyPromise;
};

export const signIn = async (returnUrl = getCurrentRoute()) => {
  if (bypassAuthEnabled || callbackInProgress || redirectInProgress) {
    return;
  }

  if (isSigninCallbackRequest()) {
    logWarn("Skipping sign-in redirect while callback route is active");
    return;
  }

  const userManager = ensureManager();
  if (!userManager) {
    window.location.assign("/login");
    return;
  }

  const safeReturnUrl = stringHasValue(returnUrl) && returnUrl.startsWith("/") ? returnUrl : "/";
  redirectInProgress = true;
  logInfo("Starting OIDC sign-in redirect", { safeReturnUrl });
  try {
    await userManager.signinRedirect({
      state: {
        returnUrl: safeReturnUrl
      }
    });
  } catch (error) {
    redirectInProgress = false;
    logError("OIDC sign-in redirect failed", error);
    throw error;
  }
};

export const signOut = async () => {
  if (bypassAuthEnabled) {
    return;
  }

  const userManager = ensureManager();
  if (!userManager) {
    window.location.assign("/logout");
    return;
  }

  await userManager.signoutRedirect();
};

export const refreshAccessToken = async () => {
  if (bypassAuthEnabled) {
    return true;
  }

  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  tokenRefreshPromise = (async () => {
    const userManager = ensureManager();
    if (!userManager) {
      return false;
    }

    try {
      const user = await userManager.getUser();
      if (!user) {
        logWarn("Silent token refresh skipped because no user is loaded");
        return false;
      }

      const refreshedUser = await userManager.signinSilent();
      const isValid =
        refreshedUser &&
        !refreshedUser.expired &&
        stringHasValue(refreshedUser.access_token);

      if (isValid) {
        authReady = true;
        logInfo("OIDC silent token refresh succeeded", { expiresAt: refreshedUser.expires_at });
        return true;
      }

      logWarn("OIDC silent token refresh returned an invalid user session");
      return false;
    } catch (error) {
      logWarn("OIDC silent token refresh failed", {
        message: error?.message,
        name: error?.name
      });
      return false;
    }
  })().finally(() => {
    tokenRefreshPromise = null;
  });

  return tokenRefreshPromise;
};

export const getAccessToken = async () => {
  if (bypassAuthEnabled) {
    return null;
  }

  const userManager = ensureManager();
  if (!userManager) {
    return null;
  }

  const user = await userManager.getUser();
  if (!user || user.expired) {
    return null;
  }

  return user.access_token;
};
